/**
 * Máscaras de formatação para campos de formulário
 * Agendario Admin - Utils
 */

/**
 * Formata telefone brasileiro com máscara progressiva (para uso em inputs).
 * Suporta fixo (10 dígitos: (XX) XXXX-XXXX) e celular (11 dígitos: (XX) XXXXX-XXXX).
 * Projetada para uso em onChange — formata à medida que o usuário digita.
 *
 * @param {string} value - Valor do input (pode conter máscara parcial)
 * @returns {string} Valor formatado progressivamente
 *
 * @example
 * maskPhoneBR('11999998888')   // '(11) 99999-8888'
 * maskPhoneBR('1199998888')    // '(11) 9999-8888'
 * maskPhoneBR('11')            // '(11) '
 */
export function maskPhoneBR(value) {
  const raw = String(value || '');
  let digits = raw.replace(/\D/g, '');
  // ⚠️ Telefone COLADO com DDI ("+55 71 99392-2512", como vem do contato do WhatsApp) tem 13
  // dígitos. Cortar em 11 guardava "55719939225" — sem os 2 últimos dígitos, irrecuperável, e o
  // WhatsApp do cliente falhava (varredura PRD 2026-09-13: 21 agendamentos assim). Tira o 55 ANTES
  // de limitar. Só em valor cru: se já está mascarado "(55) 9…", é DDD 55 real + dígito a mais.
  if (digits.length > 11 && digits.startsWith('55') && !raw.trimStart().startsWith('(')) {
    digits = digits.slice(2);
  }
  digits = digits.slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/^(\d{0,2})(\d{0,4})(\d{0,4}).*/, (_, p1, p2, p3) => {
      let out = '';
      if (p1) out += `(${p1}`;
      if (p1 && p1.length === 2) out += ') ';
      if (p2) out += p2;
      if (p3) out += `-${p3}`;
      return out.trim();
    });
  }
  return digits.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
}

/**
 * Formata telefone brasileiro
 * @param {string} value - Valor do input
 * @returns {string} - Telefone formatado (99) 99999-9999
 */
export const phoneMask = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

/**
 * Formata CPF brasileiro
 * @param {string} value - Valor do input
 * @returns {string} - CPF formatado 999.999.999-99
 */
export const cpfMask = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

/**
 * Formata CEP brasileiro
 * @param {string} value - Valor do input
 * @returns {string} - CEP formatado 99999-999
 */
export const cepMask = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{3})\d+?$/, '$1');
};

/**
 * Formata moeda brasileira (R$)
 * @param {string|number} value - Valor a ser formatado
 * @returns {string} - Moeda formatada R$ 1.234,56
 */
export const currencyMask = (value) => {
  const numericValue = typeof value === 'string' ? value.replace(/\D/g, '') : String(value);
  const floatValue = parseFloat(numericValue) / 100;
  
  return floatValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

/**
 * Remove máscara de telefone (retorna apenas dígitos)
 * @param {string} value - Telefone formatado
 * @returns {string} - Apenas dígitos
 */
export const unmaskPhone = (value) => {
  return value.replace(/\D/g, '');
};

/**
 * Remove máscara de CPF (retorna apenas dígitos)
 * @param {string} value - CPF formatado
 * @returns {string} - Apenas dígitos
 */
export const unmaskCpf = (value) => {
  return value.replace(/\D/g, '');
};

/**
 * Remove máscara de CEP (retorna apenas dígitos)
 * @param {string} value - CEP formatado
 * @returns {string} - Apenas dígitos
 */
export const unmaskCep = (value) => {
  return value.replace(/\D/g, '');
};

/**
 * Valida CPF brasileiro
 * @param {string} cpf - CPF a ser validado
 * @returns {boolean} - true se válido, false caso contrário
 */
export const validateCpf = (cpf) => {
  const cleanCpf = unmaskCpf(cpf);
  
  if (cleanCpf.length !== 11 || /^(\d)\1{10}$/.test(cleanCpf)) {
    return false;
  }

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCpf.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCpf.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(10, 11))) return false;

  return true;
};
